import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@material-ui/core';
import ReactInputMask from 'react-input-mask';
import { useParams } from 'react-router';
import AddJurisdiction from '../SharedComponents/AddJurisdiction';
import AddJurisdictionPopup from '../AddJurisdictionPopup/AddJurisdictionPopup';
import { InputTextWithLabel } from '../../../molecules';
import { WikiJurisdictionType } from '../../../api/jurisdiction';
import schema from '../WikiDetails/schema';
import { RootState } from '../../../redux/root';
import {
  useGetAllJurisdictionsQuery,
  useGetJurisdictionByIdQuery,
  useUpdateJurisdictionMutation,
} from '../../../redux/rtkQuery/apiWikiSlice';
import { objectKeysToSnakeCase } from '../../../utils';
import { setSelectedJurisdiction } from '../../../redux/slices/wikiSlice';
import TableAreaCollection from '../WikiDetails/TableAreaCollection';
import Permitting from '../Permitting/Permitting';
import Inspections from '../Inspections/Inspections';
import WikiNotes from '../WikiNotes/WikiNotes';
import { BottomControls, ControlRow } from '../WikiDetails/WikiDetails';
import StatesDropdownModified from '../SharedComponents/StateDropdownModified';
import JurisdictionWebsite from './JurisdictionWebsite';
import { urlRegex } from '../../../regex';

const WikiJurisdiction = () => {
  // @ts-ignore
  const { wikiId } = useParams();
  const dispatch = useDispatch();
  const [param, setParam] = useState<any>(undefined);
  const [disableSave, setDisableSave] = useState<boolean>(true);
  const [show, setShow] = useState<boolean>(false);
  const [juriWebsitError, setJuriWebsitError] = useState<string>('');
  const { data: wikiDetails } = useGetJurisdictionByIdQuery(param, { skip: param === undefined });
  const juri = useSelector((s: RootState) => s.wikiSlice.selectedJurisdiction);
  const activeTab = useSelector((s: RootState) => s.wikiSlice.activeTab);
  const { register, handleSubmit, setValue, formState: { isDirty }, watch, errors, control, reset } = useForm<WikiJurisdictionType>({
    resolver: yupResolver(schema),
    defaultValues: juri || ({} as WikiJurisdictionType),
  });
  const _jurisdictions = watch('jurisdictions');
  const _county = watch('county');
  const _address = watch('address');
  const _city = watch('city');
  const _state = watch('state');
  const _zipCode = watch('zipCode');
  const _boNumber = watch('boNumber');
  const _boName = watch('boName');
  const _boEmail = watch('boEmail');

  useEffect(() => {
    if (isDirty) {
      setDisableSave(false);
    }
  }, [_jurisdictions, _county, _address, _city, _state, _zipCode, _boNumber, _boName, _boEmail]);
  const [updateJurisdiction] = useUpdateJurisdictionMutation();

  useEffect(() => {
    setParam({ id: wikiId });
  }, [wikiId]);

  useEffect(() => {
    if (juri.state && _state !== juri.state) {
      setDisableSave(false);
    }
  }, [_state]);

  useEffect(() => {
    if (wikiDetails) {
      dispatch(setSelectedJurisdiction(wikiDetails));
      reset(wikiDetails);
    }
  }, [wikiDetails]);

  useEffect(() => {
    setShow(false);
  }, [activeTab]);

  const onSubmit = async data => {
    setJuriWebsitError('');
    if (!juri.jurisdictionWebsite) {
      setJuriWebsitError('Jurisdiction Website is required!');
      return;
    }

    if (!urlRegex.test(juri.jurisdictionWebsite)) {
      setJuriWebsitError('Enter a valid url starts with http:// or https://');
      return;
    }

    let payload = { ...data, id: wikiId, jurisdictionWebsite: juri.jurisdictionWebsite };
    await updateJurisdiction(objectKeysToSnakeCase(payload));
    payload = { ...wikiDetails, ...payload };
    dispatch(setSelectedJurisdiction(payload));
    setShow(false);
    setDisableSave(true);
  };
  return (
    <>
      <AddJurisdictionPopup />
      <AddJurisdiction />
      <MainHeader>Jurisdiction Details</MainHeader>
      <form autoComplete="off" aria-autocomplete="none" onSubmit={handleSubmit(onSubmit, err => console.log(err))}>
        <Controls>
          <TopControls>
            <ControlRow>
              <InputTextWithLabel
                isRequired
                inputRef={register}
                error={errors.jurisdictions}
                name="jurisdictions"
                label="Jurisdiction Name"
              />
              <JurisdictionWebsite setValue={setValue} juriWebsitErrorMessage={juriWebsitError} />
              <InputTextWithLabel isRequired inputRef={register} error={errors.county} name="county" label="County" />
            </ControlRow>
            <BottomControls>
              <Button variant="outlined" onClick={() => setShow(!show)}>
                {show ? 'HIDE' : 'Show More'}
              </Button>
            </BottomControls>
            <Collapsable show={show}>
              <ControlRow>
                <InputTextWithLabel isRequired inputRef={register} error={errors.address} name="address" label="Address" />
              </ControlRow>
              <ControlRow>
                <InputTextWithLabel isRequired inputRef={register} error={errors.city} name="city" label="City" />
                <StateWrapper>
                  <StatesDropdownModified control={control} setValue={setValue} watch={watch} errors={errors} />
                </StateWrapper>

                <InputTextWithLabel isRequired inputRef={register} error={errors.zipCode} name="zipCode" label="Zip" />
              </ControlRow>
              <ControlRow>
                <Controller
                  control={control}
                  defaultValue=""
                  name="boNumber"
                  render={props => (
                    <ReactInputMask
                      mask="999-999-9999"
                      maskChar=" "
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                    >
                      {() => <InputTextWithLabel error={errors.boNumber} label="BO Phone #" />}
                    </ReactInputMask>
                  )}
                />
                <InputTextWithLabel isRequired inputRef={register} error={errors.boName} name="boName" label="BO Name" />
                <InputTextWithLabel isRequired inputRef={register} error={errors.boEmail} name="boEmail" label="BO Email" />
              </ControlRow>
              <ControlRow>
                <BottomControls>
                  <Button variant="contained" type="submit" color="primary" disabled={disableSave}>
                    Save
                  </Button>
                </BottomControls>
              </ControlRow>
            </Collapsable>
          </TopControls>
        </Controls>
      </form>
      {activeTab === 'None' && <TableAreaCollection />}
      {activeTab === 'P' && <Permitting />}
      {activeTab === 'I' && <Inspections />}
      {activeTab === 'N' && <WikiNotes />}
    </>
  );
};

export default WikiJurisdiction;

const MainHeader = styled.div`
  font-weight: 700;
  font-size: 1.125rem;
  color: var(--mainText);
  margin: 0 0 20px;
`;

const Controls = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 24px;
  margin-bottom: 20px;
`;

const Collapsable = styled.div<{ show: boolean }>`
  height: ${({ show }) => (show ? '391px' : '2px')};
  transition: height 0.5s ease-in-out;
  overflow: hidden;
`;

const TopControls = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const StateWrapper = styled.div`
  width: 100%;
`;
